/**
 * The `entryMap` lookup key for a generic entry (s179, G4 finding 1 root-cause fix).
 *
 * 🔴 WHY THIS EXISTS. `GenericCategoryPage` builds its `entryMap` keyed by `fieldId` for card
 * entries and `` `${fieldId}:${rowId}` `` for per-row entries, and eighteen call sites reassemble
 * that key inline. In s179 one of them — the decline-reason reader — lost its interpolations to a
 * scripted edit and shipped as:
 *
 *     entryMap[`:`]
 *
 * 🔴 THE PROPERTY THAT MADE IT DANGEROUS: a template literal that loses its `${…}` parts is STILL A
 * VALID TEMPLATE LITERAL. It compiled, it typechecked, `tsc` was clean, 2435 tests passed, and the
 * expression quietly looked up a key that can never exist — so every per-row decline read `null`
 * and the product told planners "You said you don't have this" about facts they had explicitly
 * chosen to withhold. Nothing in the type system or the suite could see it, because nothing was
 * wrong with the CODE; what was wrong was the STRING.
 *
 * Extracting the key gives that string one home and one test.
 *
 * 🔴 s191 (D-191.8): all inline constructions of this key route through this builder. Two
 * different-key sites are allowlisted by name in `check:entry-key` — they are NOT this key
 * (one appends a trailing colon on the card branch, one uses a `__card__` sentinel), so
 * migrating them would change behaviour. The ratchet holds the zero.
 *
 * Kept deliberately trivial. Its value is not the logic — it is that the logic is now somewhere a
 * test can import rather than re-type, so a guard tests the real thing instead of a copy of it.
 */

/**
 * Key an entry by `(fieldId, rowId)`. `rowId === null` is the CARD case and keys on the field alone,
 * matching how `entryMap` is built.
 */
export function entryMapKey(fieldId: string, rowId: string | null): string {
  return rowId === null ? fieldId : `${fieldId}:${rowId}`;
}
